import React, { ReactNode, useCallback } from 'react';

import { RootStateOrAny, useSelector } from 'react-redux';
import styled, { FlattenSimpleInterpolation } from 'styled-components';

import { OffCanvasMenu } from '@techstyle/react-components-legacy';

import { desktop, mobileDevice, tabletDevice } from '../../styles';
import { hasOnlyMembershipCartItem as hasOnlyMembershipCartItemSelector } from '../../utils/selectors';
import { Component as EmptyCartMessage } from '../EmptyCartMessage';
import { useQuickViewContext } from '../QuickViewProvider';

import { useDrawerState } from './useDrawerState';

const StyledOffCanvasMenu = styled(OffCanvasMenu)`
  > .Overlay {
    z-index: ${({ theme }) => theme.zIndex.overlay};

    ${desktop`
      ${({ isQuickModalOpen }) =>
        isQuickModalOpen &&
        `
          pointer-events: none;
        `};
    `}

    max-width: 100vw;
    background-color: ${({ theme }) => theme.colors.black}00;
    transition: 0.2s ease-in-out background-color;

    &.ReactModal__Overlay--after-open {
      background-color: ${({ theme }) => theme.colors.black}1A;
    }

    > .Modal {
      box-shadow: ${props => `0 4px 8px ${props.theme.colors.lightShadow1}`}
      overflow: hidden;
      align-self: flex-start;
    }
  }
`;

export function Drawer({ children }) {
  const [isDrawerOpen, setIsDrawerOpen] = useDrawerState();
  const { isOpen: isQuickModalOpen } = useQuickViewContext();

  const closeDrawer = useCallback(() => {
    if (!isQuickModalOpen) {
      setIsDrawerOpen(false);
    }
  }, [isQuickModalOpen, setIsDrawerOpen]);
  return (
    <StyledOffCanvasMenu
      right
      isOpen={isDrawerOpen}
      onRequestClose={closeDrawer}
      closeButton={null}
      isQuickModalOpen={isQuickModalOpen}
    >
      {children}
    </StyledOffCanvasMenu>
  );
}

const ContentWrapper = styled.div<{
  hasOverlay: boolean;
  contentWrapperStyling?: FlattenSimpleInterpolation;
}>`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: ${props => props.theme.colors.white};
  pointer-events: ${props => (props.hasOverlay ? 'none' : 'auto')};

  width: 300px;

  ${desktop`
    display: flex;
    flex-direction: column;
  `}

  ${tabletDevice`
    display: flex;
    flex-direction: column;
    height: 100dvh;
  `}

  ${mobileDevice`
    height: 100dvh;
  `}

  ${({ contentWrapperStyling }) => contentWrapperStyling};
`;

export function Content({
  children,
  contentWrapperStyling,
}: {
  children: ReactNode;
  contentWrapperStyling?: FlattenSimpleInterpolation;
}) {
  const hasOverlay = useSelector(
    ({ checkout }: RootStateOrAny) => checkout.loading
  );
  return (
    <ContentWrapper
      data-testid="CartDrawerContent"
      data-autotag="mini-cart-modal"
      className="bfx-minicart-container"
      hasOverlay={hasOverlay}
      contentWrapperStyling={contentWrapperStyling}
    >
      {children}
    </ContentWrapper>
  );
}

const CartDrawerBodyWrapper = styled.div`
  flex: 0 1 auto;
  overflow-x: hidden;
  overflow-y: auto;
`;

export function CartDrawerBody({ children }) {
  const cart = useSelector(({ checkout }: RootStateOrAny) => checkout);
  const hasOnlyMembershipItem = useSelector(hasOnlyMembershipCartItemSelector);

  // When the site initially loads, we hit `cart/items/count` which only returns
  // `cart.itemCount` but not `cart.items`. The `hasNotLoadedCart` check allows
  // us to display a custom UI if the cart is not properly loaded yet.
  const hasNotLoadedCart = cart.items.length === 0 && cart.itemCount > 0;
  const emptyCartMessage = hasNotLoadedCart ? null : (
    <EmptyCartMessage className={undefined} />
  );

  if (cart.itemCount <= 0 || hasOnlyMembershipItem || hasNotLoadedCart) {
    return emptyCartMessage;
  }
  return <CartDrawerBodyWrapper>{children}</CartDrawerBodyWrapper>;
}
